import type { Blackboard, Listener } from "./Blackboard";
import type { EventBus } from "./EventBus";
import type { ObjectEntity } from "./Interpreter";
import { resolveObjectSprite, isObjectHidden } from "./objectSprites";

/**
 * Reactive driver for in-scene object sprites (analogous to AchievementEngine).
 *
 * Tracks objects currently shown (via `object.show`) and, on any Blackboard
 * write, re-resolves each one:
 *   - `hidden_when` now true   → emit `object.hide`
 *   - active variant changed    → emit `object.update` (re-skin in place)
 *
 * Room auto-placement re-derivation (deriving which objects belong in the
 * current room and emitting `object.show` for them) is deferred to P5, where the
 * shelf placement system supplies the coordinates. Until then this engine reacts
 * over objects shown explicitly via `%object% … %at%`.
 */
export class ObjectSpriteEngine {
  private _objects: Record<string, ObjectEntity> = {};
  /** id → currently-rendered asset path */
  private _shown = new Map<string, string>();
  private _unsubs: Array<() => void> = [];

  constructor(private _bb: Blackboard, private _bus: EventBus) {}

  load(objects: Record<string, ObjectEntity>): void {
    this.destroy();
    this._objects = objects;
    if (Object.keys(objects).length === 0) return; // nothing to react over

    this._unsubs.push(
      this._bus.on("object.show", (d) => this._shown.set(d.id, d.asset)),
      this._bus.on("object.hide", (d) => this._shown.delete(d.id)),
    );

    const listener: Listener = () => this._reevaluate();
    this._bb.listenAll(listener);
    this._unsubs.push(() => this._bb.removeGlobalListener(listener));
  }

  private _reevaluate(): void {
    // Snapshot: emitting object.hide mutates _shown via the subscriber above.
    for (const [id, asset] of [...this._shown]) {
      const entity = this._objects[id];
      if (isObjectHidden(entity, this._bb)) {
        this._shown.delete(id);
        this._bus.emit("object.hide", { id });
        continue;
      }
      const next = resolveObjectSprite(entity, this._bb);
      if (next && next !== asset) {
        this._shown.set(id, next);
        this._bus.emit("object.update", { id, asset: next });
      }
    }
  }

  destroy(): void {
    for (const u of this._unsubs) u();
    this._unsubs = [];
    this._shown.clear();
    this._objects = {};
  }
}
